import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { Mail, Phone, Award, Users, TrendingDown, Clock, Gift } from 'lucide-react';
import { Card, CardContent } from '../components/ui/card';

const API = process.env.REACT_APP_BACKEND_URL;

const ContactPage = () => {
  const [referrerName, setReferrerName] = useState(null);

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const code = params.get('ref');
    if (code) {
      // Store code for later use during form submission
      sessionStorage.setItem('lma_referral_code', code);
      axios.post(`${API}/api/public/referral/track`, { code })
        .then(res => setReferrerName(res.data.referrer_name))
        .catch(() => {});
    }
  }, []);

  const trustBadges = [
    { icon: <Award size={22} />, title: '20+ Years', subtitle: 'Experience' },
    { icon: <Users size={22} />, title: '500+', subtitle: 'Clients' },
    { icon: <TrendingDown size={22} />, title: '$10M+', subtitle: 'Saved' },
    { icon: <Clock size={22} />, title: '48hr', subtitle: 'Response' }
  ];

  return (
    <main className="page-content" data-testid="contact-page">
      {/* Referral banner */}
      {referrerName && (
        <div style={{
          background: 'linear-gradient(135deg, #F0FDFA 0%, #ECFDF5 100%)',
          borderBottom: '1px solid #A7F3D0',
          padding: '0.875rem 1rem',
          textAlign: 'center',
          fontSize: '0.938rem',
          color: '#065F46',
          display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8,
        }} data-testid="referral-banner">
          <Gift size={18} /> You were referred by <strong>{referrerName}</strong>
        </div>
      )}
      {/* Page Header */}
      <section className="page-header-with-image">
        <div className="page-header-overlay"></div>
        <div className="container page-header-content">
          <span className="overline">Get In Touch</span>
          <h1 className="page-title" style={{marginTop: '0.5rem'}}>Request a Consultation</h1>
          <p className="page-subtitle">
            Tell us about your organization and energy situation. There's no obligation -- we're happy to discuss whether our services might be a good fit.
          </p>
        </div>
      </section>

      {/* Trust Badges */}
      <section className="contact-trust-section">
        <div className="container">
          <div className="contact-trust-grid">
            {trustBadges.map((badge, index) => (
              <div key={index} className="contact-trust-item" data-testid={`contact-trust-${index}`}>
                <div className="contact-trust-icon">{badge.icon}</div>
                <div className="contact-trust-content">
                  <span className="contact-trust-title">{badge.title}</span>
                  <span className="contact-trust-subtitle">{badge.subtitle}</span>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Contact Section */}
      <section className="section section-light">
        <div className="container">
          <div className="contact-info-centered">
            <Card className="contact-info-card-large">
              <CardContent className="p-10">
                <h3 className="contact-info-title-large">Get In Touch</h3>
                <p className="contact-intro-text-large">
                  Ready to take control of your energy costs? Contact us to schedule a consultation and learn how we can help your organization reduce utility expenses.
                </p>
                <div className="contact-info-items-large">
                  <div className="contact-info-item-large contact-email-centered">
                    <div className="contact-icon-wrapper">
                      <Phone size={24} className="contact-info-icon-large" />
                    </div>
                    <div>
                      <p className="contact-info-label-large">Phone</p>
                      <a href="tel:+16122763255" className="contact-info-value-link-large" data-testid="contact-phone-link">
                        612-276-3255
                      </a>
                    </div>
                  </div>
                  <div className="contact-info-item-large contact-email-centered">
                    <div className="contact-icon-wrapper">
                      <Mail size={24} className="contact-info-icon-large" />
                    </div>
                    <div>
                      <p className="contact-info-label-large">Email</p>
                      <a href="mailto:sales@lastmileadvisors.com" className="contact-info-value-link-large" data-testid="contact-email-link">
                        sales@lastmileadvisors.com
                      </a>
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Awards inline */}
            <div style={{marginTop: '2rem'}}>
              <div className="awards-banner" data-testid="contact-awards">
                {[
                  { image: '/award-diamond.png', title: 'Diamond Award', subtitle: '2026' },
                  { image: '/award-customer-service.png', title: 'Top 10 Service', subtitle: 'Front Runner' },
                  { image: '/award-five-star.png', title: 'Five Star Energy', subtitle: 'Brick, Inc' }
                ].map((award, index) => (
                  <div key={index} className="award-badge">
                    <img src={award.image} alt={award.title} />
                    <div className="award-badge-text">
                      <span>{award.title}</span>
                      <span>{award.subtitle}</span>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            <div className="next-steps-centered">
              <h4 className="next-steps-title-large">What Happens Next?</h4>
              <div className="next-steps-grid">
                <div className="next-step-item">
                  <span className="next-step-number">1</span>
                  <p>Send us an email with your inquiry</p>
                </div>
                <div className="next-step-item">
                  <span className="next-step-number">2</span>
                  <p>A team member will schedule a brief call</p>
                </div>
                <div className="next-step-item">
                  <span className="next-step-number">3</span>
                  <p>We'll discuss your situation and determine if we can help</p>
                </div>
                <div className="next-step-item">
                  <span className="next-step-number">4</span>
                  <p>If there's a fit, we'll outline recommended next steps</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>
    </main>
  );
};

export default ContactPage;
